import { Loader } from "@mantine/core";
import { IBim360FolderContent, IFolderAgGrid } from "@mctypes/global.types";
import React, { useEffect, useState } from "react";
import {
  doGetBim360FolderContent,
  doGetBim360RootFolders,
  doGetParentOfBim360Folder,
} from "../../app/routes/admin/restQueries";
import FolderSelector from "../FolderAndFiles/FolderSelector";

const BIM360FolderSelector: React.FC<{
  projectId: string;
  bim360ProjectId: string;
  onSelectedAgFolderUrn: (folderAg: IFolderAgGrid) => void;
}> = ({ projectId, bim360ProjectId, onSelectedAgFolderUrn }) => {
  const [bim360Folders, setBim360Folders] = useState<IBim360FolderContent[]>(null);
  const [currentBim360Folder, setCurrentBim360Folder] = useState<IBim360FolderContent>();
  const [highlightedFolderBim360, setHighlighetedFolderBim360] = useState<IBim360FolderContent>();
  const [foldersLoading, setFoldersLoading] = useState<boolean>(false);

  useEffect(() => {
    init();
  }, []);

  const init = async () => {
    setFoldersLoading(true);
    try {
      const rootFolders = await doGetBim360RootFolders(projectId, bim360ProjectId);
      const projectFilesFolder = rootFolders.find((folder) => folder.name === "Project Files");

      const _bim360Folders: IBim360FolderContent[] = rootFolders.map((folder) => {
        const rootFolder: IBim360FolderContent = {
          id: folder.id || folder.urn,
          attributes: {
            displayName: folder.name,
          },
        };
        return rootFolder;
      });
      setBim360Folders(_bim360Folders);
      folderSelection(formatBim360ToAgFolder(projectFilesFolder));
    } catch (error) {
      console.log("failed to connect to bim360");
      console.warn(error);
    } finally {
      setFoldersLoading(false);
    }
  };

  const folderSelection = async (folderAg: IFolderAgGrid) => {
    setHighlighetedFolderBim360(formatAgFolderToBim360(folderAg));
    onSelectedAgFolderUrn(folderAg);
  };

  const goToFolder = async (folderAg: IFolderAgGrid) => {
    const isRoot = folderAg.name.toUpperCase().endsWith("ROOT-FOLDER");
    setCurrentBim360Folder(isRoot === false ? formatAgFolderToBim360(folderAg) : null);
    setFoldersLoading(true);
    try {
      const folderContent = await doGetBim360FolderContent(projectId, bim360ProjectId, folderAg.id);
      const _bim360Folders = folderContent.data.filter((content) => content.type === "folders");
      setBim360Folders(_bim360Folders);
    } finally {
      setFoldersLoading(false);
    }
  };

  const goToPreviousFolder = async () => {
    setFoldersLoading(true);
    try {
      const parentBim360Folder = await doGetParentOfBim360Folder(
        projectId,
        bim360ProjectId,
        currentBim360Folder.id || currentBim360Folder.urn,
      );
      setHighlighetedFolderBim360(null);
      await goToFolder({
        name: parentBim360Folder.attributes?.displayName,
        id: parentBim360Folder.id || parentBim360Folder.urn,
      });
    } catch (error) {
      setFoldersLoading(false);
      throw error;
    }
  };

  if (bim360Folders == null && foldersLoading === true) {
    return (
      <div className="d-flex align-items-center justify-content-center h-100 text-muted">
        <Loader className="mr-2" size="sm" />
        <span>Henter mapper...</span>
      </div>
    );
  }

  return (
    <FolderSelector
      currentFolder={formatBim360ToAgFolder(currentBim360Folder)}
      folders={bim360Folders == null ? [] : bim360Folders?.map((folder) => formatBim360ToAgFolder(folder)) || []}
      onSelectActiveFolder={folderSelection}
      onGoToNextFolder={goToFolder}
      highlightedFolder={formatBim360ToAgFolder(highlightedFolderBim360)}
      goBack={() => goToPreviousFolder()}
      canGoBack={currentBim360Folder != null}
      isLoading={foldersLoading}
    />
  );
};

export default BIM360FolderSelector;

const formatBim360ToAgFolder = (bim360Folder: IBim360FolderContent): IFolderAgGrid => {
  if (bim360Folder == null) {
    return null;
  }
  return {
    id: bim360Folder.urn || bim360Folder.id,
    name: bim360Folder.attributes?.name || bim360Folder.attributes?.displayName,
  };
};

const formatAgFolderToBim360 = (agFolder: IFolderAgGrid): IBim360FolderContent => {
  if (agFolder == null) {
    return null;
  }
  return {
    id: agFolder.id,
    attributes: {
      displayName: agFolder.name,
    },
  };
};
